import React, { useEffect, useMemo, useRef } from 'react'
import { Alert, Button, Flex, Typography } from 'antd'
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { ColDef } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../../types/company/Company'
import { SiblingCandidates } from '../../utils/groupSuggestionsBySibling'
import { RowAction } from '../../utils/buildSiblingDecision'
import { mappingGridTheme, MAPPING_GRID_HEADER_HEIGHT } from '../../utils/mappingGridTheme'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import StepHeading from './StepHeading'
import CellSignalLegend from './CellSignalLegend'
import {
  ActionCellRenderer,
  CandidateCellRenderer,
  WizardSuggestionsGridContext
} from './wizardSuggestionsCellRenderers'

interface WizardStepSuggestionsProps {
  target: Company
  siblings: Company[]
  candidatesBySibling: Map<string, SiblingCandidates>
  rowActions: Record<string, RowAction>
  onRowActionChange: (siblingId: string, action: RowAction) => void
  loading?: boolean
  error?: boolean
  onBack: () => void
  onContinue: () => void
}

const WizardStepSuggestions: React.FC<WizardStepSuggestionsProps> = ({
  target,
  siblings,
  candidatesBySibling,
  rowActions,
  onRowActionChange,
  loading,
  error,
  onBack,
  onContinue
}) => {
  const { t } = useTranslation()
  const gridRef = useRef<AgGridReact>(null)

  // The candidate cells read from grid context, which ag-grid does not re-render on its own.
  // When suggestions finish loading `candidatesBySibling` goes from empty to populated, so force
  // the cells (and their auto heights) to re-evaluate.
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true })
    gridRef.current?.api?.resetRowHeights()
  }, [candidatesBySibling])

  const acceptCount = siblings.filter(sibling => rowActions[sibling.id] === 'accept').length
  const reviewCount = siblings.filter(sibling => rowActions[sibling.id] === 'review').length
  const skipCount = siblings.filter(sibling => rowActions[sibling.id] === 'skip').length

  const columnDefs = useMemo<ColDef<Company>[]>(
    () => [
      {
        colId: 'sibling',
        headerName: t('groupCustomerMapping:wizardSuggestionsSibling', 'Sisaryhtiö'),
        field: 'name',
        flex: 1
      },
      {
        colId: 'customer',
        headerName: t('groupCustomerMapping:wizardSuggestionsCustomer', 'Asiakkaana'),
        flex: 1,
        autoHeight: true,
        cellRenderer: CandidateCellRenderer,
        cellRendererParams: { role: 'customer' }
      },
      {
        colId: 'supplier',
        headerName: t('groupCustomerMapping:wizardSuggestionsSupplier', 'Toimittajana'),
        flex: 1,
        autoHeight: true,
        cellRenderer: CandidateCellRenderer,
        cellRendererParams: { role: 'supplier' }
      },
      {
        colId: 'action',
        headerName: t('groupCustomerMapping:wizardSuggestionsAction', 'Toimenpide'),
        width: 120,
        cellRenderer: ActionCellRenderer
      }
    ],
    [t]
  )

  const gridContext = useMemo<WizardSuggestionsGridContext>(
    () => ({ targetId: target.id, candidatesBySibling, rowActions, onRowActionChange }),
    [target.id, candidatesBySibling, rowActions, onRowActionChange]
  )

  return (
    <>
      <StepHeading
        title={t('groupCustomerMapping:wizardSuggestionsTitle', 'Ehdotetut vastineet · {{target}}', {
          target: target.name
        })}
        description={t(
          'groupCustomerMapping:wizardSuggestionsDescription',
          'Jokainen sisaryhtiö tarvitsee erikseen asiakas- ja toimittajavastineen. Rivi hyväksytään vasta kun molemmat on valittu.'
        )}
      />
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('groupCustomerMapping:wizardSuggestionsErrorTitle', 'Ehdotusten haku epäonnistui')}
          description={t(
            'groupCustomerMapping:wizardSuggestionsErrorDescription',
            'Osaa ehdotuksista ei saatu ladattua. Yritä myöhemmin uudelleen — jatkaminen voi tuottaa puutteellisia vastineita.'
          )}
        />
      )}
      <Flex justify="space-between">
        <CellSignalLegend />

        <Typography.Text style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          {t(
            'groupCustomerMapping:wizardSuggestionsSummary',
            '{{accept}} hyväksy · {{review}} tarkista · {{skip}} ohita',
            {
              accept: acceptCount,
              review: reviewCount,
              skip: skipCount
            }
          )}
        </Typography.Text>
      </Flex>
      <div style={{ marginBottom: 16 }}>
        <CustomAgGrid
          ref={gridRef}
          theme={mappingGridTheme}
          domLayout="autoHeight"
          columnDefs={columnDefs}
          rowData={siblings}
          context={gridContext}
          getRowId={params => params.data.id}
          headerHeight={MAPPING_GRID_HEADER_HEIGHT}
          loading={loading}
        />
      </div>

      <Flex justify="space-between" align="center">
        <Flex align="center" gap={14}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            {t('groupCustomerMapping:wizardSuggestionsBack', 'Edellinen')}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              'groupCustomerMapping:wizardSuggestionsFooterNote',
              'Linkitykset kirjataan yksitellen — keskeytys tai virhe voi jättää osan tekemättä.'
            )}
          </Typography.Text>
        </Flex>
        <Button type="primary" icon={<ArrowRightOutlined />} iconPosition="end" onClick={onContinue} disabled={error}>
          {t('groupCustomerMapping:wizardSuggestionsContinue', 'Jatka {{count}} käsiteltävää', {
            count: reviewCount
          })}
        </Button>
      </Flex>
    </>
  )
}

export default WizardStepSuggestions
